/**
 * Offline Context Provider
 * Manages offline state and sync operations across the app
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import unifiedOfflineService from '@/services/unifiedOfflineService';
import { toast } from 'sonner';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInitialized, setIsInitialized] = useState(false);
  const [syncStats, setSyncStats] = useState({ pending: 0, synced: 0, failed: 0 });
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Initialize IndexedDB
  useEffect(() => {
    const initDB = async () => {
      try {
        await unifiedOfflineService.init();
        setIsInitialized(true);
        console.log('[OfflineContext] Database initialized');
        
        // Load initial sync stats
        const stats = await unifiedOfflineService.getQueueStats();
        setSyncStats(stats);
      } catch (error) {
        console.error('[OfflineContext] Failed to initialize:', error);
        toast.error('Failed to initialize offline storage');
      }
    };
    
    initDB();
  }, []);

  // Subscribe to offline storage events
  useEffect(() => {
    if (!isInitialized) return;

    const unsubscribe = unifiedOfflineService.subscribe((event) => {
      if (event.isOnline !== undefined) {
        setIsOnline(event.isOnline);
        
        if (event.isOnline) {
          toast.success('Back online! Syncing data...');
        } else {
          toast.warning('You are offline. Changes will be saved locally.');
        }
      }
      
      if (event.type === 'syncComplete') {
        setIsSyncing(false);
        setLastSyncTime(new Date());
        
        if (event.results.synced > 0) {
          toast.success(`Synced ${event.results.synced} items`);
        }
        if (event.results.failed > 0) {
          toast.error(`Failed to sync ${event.results.failed} items`);
        }
        
        // Refresh stats
        refreshSyncStats();
      }
    });

    return () => unsubscribe();
  }, [isInitialized]);

  // Refresh sync queue statistics
  const refreshSyncStats = useCallback(async () => {
    if (!isInitialized) return;
    const stats = await unifiedOfflineService.getQueueStats();
    setSyncStats(stats);
  }, [isInitialized]);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!isInitialized || !isOnline || isSyncing) return;
    
    setIsSyncing(true);
    try {
      await unifiedOfflineService.syncAll();
    } catch (error) {
      console.error('[OfflineContext] Sync failed:', error);
      toast.error('Sync failed');
      setIsSyncing(false);
    }
  }, [isInitialized, isOnline, isSyncing]);

  // Save incident offline
  const saveIncidentOffline = useCallback(async (incidentData) => {
    if (!isInitialized) {
      throw new Error('Offline storage not initialized');
    }
    
    const result = await unifiedOfflineService.saveIncidentOffline(incidentData);
    await refreshSyncStats();
    toast.info('Incident saved offline. Will sync when online.');
    return result;
  }, [isInitialized, refreshSyncStats]);

  // Save hazard offline
  const saveHazardOffline = useCallback(async (hazardData) => {
    if (!isInitialized) {
      throw new Error('Offline storage not initialized');
    }
    
    const result = await unifiedOfflineService.saveHazardOffline(hazardData);
    await refreshSyncStats();
    toast.info('Hazard report saved offline. Will sync when online.');
    return result;
  }, [isInitialized, refreshSyncStats]);

  // Save wildlife strike report offline
  const saveWildlifeStrikeOffline = useCallback(async (strikeData) => {
    if (!isInitialized) {
      throw new Error('Offline storage not initialized');
    }
    const result = await unifiedOfflineService.saveWildlifeStrikeOffline(strikeData);
    await refreshSyncStats();
    toast.info('Wildlife strike saved offline. Will sync when online.');
    return result;
  }, [isInitialized, refreshSyncStats]);

  // Get pending items
  const getPendingItems = useCallback(async () => {
    if (!isInitialized) return [];
    return unifiedOfflineService.getPendingSyncItems();
  }, [isInitialized]);

  // Cache data for offline access
  const cacheData = useCallback(async (key, type, data) => {
    if (!isInitialized) return;
    return unifiedOfflineService.cacheData(key, type, data);
  }, [isInitialized]);

  // Get cached data
  const getCachedData = useCallback(async (key) => {
    if (!isInitialized) return null;
    return unifiedOfflineService.getCachedData(key);
  }, [isInitialized]);

  // Save auth token for offline sync
  const saveAuthToken = useCallback(async (token) => {
    if (!isInitialized) return;
    return unifiedOfflineService.saveUserData('authToken', token);
  }, [isInitialized]);

  const value = {
    isOnline,
    isInitialized,
    isSyncing,
    syncStats,
    lastSyncTime,
    triggerSync,
    saveIncidentOffline,
    saveHazardOffline,
    saveWildlifeStrikeOffline,
    getPendingItems,
    cacheData,
    getCachedData,
    saveAuthToken,
    refreshSyncStats
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
}

export default OfflineContext;
